// GA4 Enhanced Ecommerce event helpers.
//
// All events are no-ops on the server (typeof window === 'undefined') and
// also no-op until gtag is loaded. The Analytics component in app/ injects
// gtag.js with strategy="afterInteractive", so by the time a user can click
// AddToCart, gtag exists. View-on-mount events use a small useEffect timer
// fallback for cases where the page hydrates faster than gtag initializes.
//
// Consent Mode v2 is wired in app/Analytics.tsx — gtag default starts as
// 'denied' for all storage axes; consent_update fires when the user picks
// a cookie banner option. We do NOT gate event firing on consent here:
// gtag itself respects consent (no PII transmitted under denied storage,
// only aggregated cookieless modeled data). Calling gtag('event', ...)
// before consent is decided is safe — the consent default holds.
//
// Documented event spec:
//   https://developers.google.com/analytics/devguides/collection/ga4/reference/events
//
// Item params follow GA4's item taxonomy:
//   item_id        SKU or product id
//   item_name      product name
//   item_brand     brand
//   item_category  fragrance gender bucket: For Her | For Him | Unisex
//   item_category2 fragrance family: fresh | floral | oriental | woody | ...
//   price          numeric price (USD)
//   quantity       integer

declare global {
  interface Window {
    gtag?:      (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export interface AnalyticsItem {
  item_id:        string;
  item_name:      string;
  item_brand?:    string;
  item_category?: string;
  item_category2?: string;
  item_variant?:  string;
  price?:         number;
  quantity?:      number;
}

function gtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function fire(name: string, params: Record<string, unknown>): void {
  if (!gtagAvailable()) {
    // In dev, surface missed events so Bradley can spot wiring gaps. In prod,
    // stay silent — a missed event isn't user-facing damage.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[ga4] (gtag not ready)', name, params);
    }
    return;
  }
  window.gtag!('event', name, params);
}

/** Parse a price string like "$24.99" or "24.99 USD" or "24.99" → 24.99. */
export function parsePrice(raw: string | number | undefined | null): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  // Strip everything that isn't a digit, dot, or minus
  const cleaned = raw.replace(/[^0-9.\-]/g, '');
  if (!cleaned) return undefined;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** Sum item prices × quantities for the `value` parameter. */
function totalValue(items: AnalyticsItem[]): number {
  return items.reduce((sum, it) => sum + (it.price ?? 0) * (it.quantity ?? 1), 0);
}

// ───────────────────────────────────────────────────────────────────
// Events
// ───────────────────────────────────────────────────────────────────

/** Product detail page view. Fire from PDP on mount. */
export function trackViewItem(item: AnalyticsItem): void {
  fire('view_item', {
    currency: 'USD',
    value:    item.price ?? 0,
    items:    [{ ...item, quantity: item.quantity ?? 1 }],
  });
}

/** Collection / shop list page view. Fire on mount with the visible items. */
export function trackViewItemList(listId: string, listName: string, items: AnalyticsItem[]): void {
  if (!items.length) return;
  fire('view_item_list', {
    item_list_id:   listId,
    item_list_name: listName,
    items:          items.map((it, i) => ({ ...it, index: i + 1 })),
  });
}

/** Click on a product card. Fire from onClick handlers. */
export function trackSelectItem(listId: string, listName: string, item: AnalyticsItem): void {
  fire('select_item', {
    item_list_id:   listId,
    item_list_name: listName,
    items:          [item],
  });
}

/** Successful add-to-cart. Fire after the line lands in the bag. */
export function trackAddToCart(item: AnalyticsItem): void {
  const qty = item.quantity ?? 1;
  fire('add_to_cart', {
    currency: 'USD',
    value:    (item.price ?? 0) * qty,
    items:    [{ ...item, quantity: qty }],
  });
}

/** Cart line removal. */
export function trackRemoveFromCart(item: AnalyticsItem): void {
  const qty = item.quantity ?? 1;
  fire('remove_from_cart', {
    currency: 'USD',
    value:    (item.price ?? 0) * qty,
    items:    [{ ...item, quantity: qty }],
  });
}

/** Cart page view. */
export function trackViewCart(items: AnalyticsItem[]): void {
  fire('view_cart', {
    currency: 'USD',
    value:    totalValue(items),
    items,
  });
}

/** Checkout initiation — user clicked the Checkout button. */
export function trackBeginCheckout(items: AnalyticsItem[]): void {
  if (!items.length) return;
  fire('begin_checkout', {
    currency: 'USD',
    value:    totalValue(items),
    items,
  });
}

/** Wishlist save. */
export function trackAddToWishlist(item: AnalyticsItem): void {
  fire('add_to_wishlist', {
    currency: 'USD',
    value:    item.price ?? 0,
    items:    [item],
  });
}

/** Search query. Fire after results are computed. */
export function trackSearch(searchTerm: string, resultCount: number): void {
  fire('search', {
    search_term: searchTerm,
    // Custom param — useful in GA4 explore reports
    result_count: resultCount,
  });
}

/**
 * Completed purchase. Fired once from /checkout/success when PlazaStack reports
 * the session paid. Caller de-dupes (the success URL can be reloaded) with
 * `markOrderTracked` / `wasOrderTracked` below.
 */
export function trackPurchase(args: {
  orderId:     string;
  total:       number;
  shipping?:   number;
  tax?:        number;
  items:       AnalyticsItem[];
}): void {
  fire('purchase', {
    transaction_id: args.orderId,
    currency:       'USD',
    value:          args.total,
    ...(args.shipping != null ? { shipping: args.shipping } : {}),
    ...(args.tax      != null ? { tax:      args.tax      } : {}),
    items:          args.items,
  });
}

const TRACKED_ORDERS_KEY = 'np:ga4-tracked-orders';

/** Returns true if this order id has been GA4-tracked already on this device. */
export function wasOrderTracked(orderId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(TRACKED_ORDERS_KEY);
    if (!raw) return false;
    const list = JSON.parse(raw) as string[];
    return Array.isArray(list) && list.includes(orderId);
  } catch {
    return false;
  }
}

export function markOrderTracked(orderId: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw  = window.localStorage.getItem(TRACKED_ORDERS_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    if (!list.includes(orderId)) {
      // Cap at 50 to keep localStorage entry small
      list.push(orderId);
      const trimmed = list.slice(-50);
      window.localStorage.setItem(TRACKED_ORDERS_KEY, JSON.stringify(trimmed));
    }
  } catch {
    // Quota / disabled — non-fatal; worst case the next page-load re-fires.
  }
}
